// Deterministic option shuffle (fairness fix).
//
// Answer options were rendered in raw DB order, and ~80% of questions store the
// correct answer as options[0] — so the correct answer sat top-left almost every
// time, making the game trivially cheatable ("always tap the first option").
//
// This shuffles options with a SEEDED PRNG keyed by the question's id, so the
// order is:
//   • not correct-biased — the correct answer lands anywhere;
//   • STABLE across re-renders (same seed → same order) so options never jump
//     mid-question;
//   • the SAME for every client on that question (seeded by a shared id), so the
//     answer grid and the reveal screen agree.
// Scoring and 50/50 compare options by VALUE, not index, so reordering is safe.

// cyrb53 string hash → 32-bit seed.
function hashSeed(str) {
  let h1 = 0xdeadbeef ^ 0;
  let h2 = 0x41c6ce57 ^ 0;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (h2 >>> 0) * 4294967296 + (h1 >>> 0);
}

// mulberry32 PRNG — deterministic [0,1) from a 32-bit seed.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Return a new array = `options` shuffled deterministically by `seed`.
 * Arrays of length < 2 are returned as a copy unchanged. Non-arrays pass through.
 * @param {Array} options
 * @param {string|number} seed  a stable per-question id
 * @returns {Array}
 */
export function shuffleQuestionOptions(options, seed) {
  if (!Array.isArray(options) || options.length < 2) {
    return Array.isArray(options) ? [...options] : options;
  }
  const rand = mulberry32(hashSeed(seed));
  const out = [...options];
  // Fisher-Yates with the seeded PRNG.
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
